/**
 * Centralized Image Utilities
 * Single source of truth for all image handling across the app.
 *
 * Rules:
 * - ONLY use `article.featured_image` — no `.image`, `.thumbnail`, etc.
 * - All components import getArticleImage() for consistency.
 * - Handles R2 absolute URLs, relative /api/uploads/..., and external URLs.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1200&h=675&fit=crop';

/**
 * Resolves an image URL:
 * - Absolute R2/CDN URLs (https://pub-*.r2.dev/...) => pass through
 * - /api/uploads/file.webp => prepend BACKEND_URL
 * - /uploads/file.webp (legacy) => convert to /api/uploads/ + prepend BACKEND_URL
 * - Old absolute paths with /uploads/ from stale domains => re-resolve
 * - External URLs => pass through
 */
function resolveImageUrl(url) {
  if (!url || typeof url !== 'string' || url.trim() === '') return null;

  const trimmed = url.trim();

  // Absolute URL (R2, Unsplash, any CDN) — use as-is
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    // Check if it's an old stale domain pointing to /uploads/ (not R2)
    if (trimmed.includes('/uploads/') && !trimmed.includes('.r2.dev/')) {
      try {
        const parsed = new URL(trimmed);
        const uploadsPath = parsed.pathname;
        if (uploadsPath.startsWith('/api/uploads/')) {
          return `${BACKEND_URL}${uploadsPath}`;
        }
        return `${BACKEND_URL}/api${uploadsPath}`;
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  // Already a full /api/uploads/ relative path
  if (trimmed.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}${trimmed}`;
  }

  // Legacy relative path — prepend /api prefix
  if (trimmed.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${trimmed}`;
  }

  // Anything else — return as-is
  return trimmed;
}

/**
 * Returns the best available image URL for an article.
 * Uses ONLY `featured_image` with a single fallback.
 */
export function getArticleImage(article) {
  if (!article) return FALLBACK_IMAGE;
  const resolved = resolveImageUrl(article.featured_image);
  return resolved || FALLBACK_IMAGE;
}

/**
 * onError handler for <img> tags — swaps to fallback on load failure.
 */
export function handleImageError(e) {
  if (e.target.src !== FALLBACK_IMAGE) {
    e.target.src = FALLBACK_IMAGE;
  }
}

export { FALLBACK_IMAGE, resolveImageUrl };
